// src/pages/SAP/index.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SapCard from "../Components/SapCard/sapcard.jsx";
import { api } from "../../services/api.js";
import Swal from 'sweetalert2';
import Menu from "../../comum/Menu/menu.jsx";
import Footer from '../../comum/Footer/index.jsx';
import {
    FaCheckDouble,
    FaTimes,
    FaCheck,
    FaUndoAlt,
    FaTag,
    FaExclamationTriangle
} from "react-icons/fa";
import './index.css';

function Sap() {
    const [tabelas, setTabelas] = useState([]);
    const [removeLoading, setRemoveLoading] = useState(false);
    const [atualizado, setAtualizado] = useState(0);
    const [selecionados, setSelecionados] = useState([]);
    const [precosEditados, setPrecosEditados] = useState({});

    const navigate = useNavigate();
    const chapa = localStorage.getItem("chapa");

    // Formatador
    const formatNumber = (num) => {
        return Number(num || 0).toLocaleString('pt-BR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    };

    // Verifica Autenticação e Permissões de Administrador SAP
    useEffect(() => {
        if (!chapa) {
            navigate("/arbor/login");
            return;
        }

        const sapAdmin = localStorage.getItem("SAPADM");
        if (sapAdmin !== "S") {
            Swal.fire({
                icon: 'error',
                title: 'Acesso Negado',
                text: 'Você não tem permissão para acessar a liberação de preços SAP.',
                confirmButtonText: 'Voltar ao Início',
                confirmButtonColor: '#991b1b',
                allowOutsideClick: false
            }).then(() => {
                navigate("/arbor/home");
            });
        }
    }, [chapa, navigate]);

    // Carrega Solicitações de Preço da API
    const carregarSolicitacoes = useCallback(() => {
        if (!chapa) return;
        setRemoveLoading(false);

        api.get(`/SAP/consulta/${chapa}`)
            .then(response => {
                setTabelas(Array.isArray(response.data) ? response.data : []);
            })
            .catch(err => {
                const msg = err.response?.data?.erro || "Não foi possível carregar as solicitações de preço.";
                Swal.fire({
                    icon: 'error',
                    title: 'Erro de Conexão',
                    text: msg,
                    confirmButtonColor: '#991b1b'
                });
                setTabelas([]);
            })
            .finally(() => {
                setRemoveLoading(true);
            });
    }, [chapa]);

    useEffect(() => {
        carregarSolicitacoes();
    }, [carregarSolicitacoes, atualizado]);

    // Evento ao Editar Preço num Card
    const onPrecoEditadoChange = (loja, codigo, sequencia, precoEditado) => {
        const key = `${loja}-${codigo}-${sequencia}`;
        setPrecosEditados(prev => {
            const originalItem = tabelas.find(item =>
                item.sap_loja === loja && item.sap_codigo === codigo && item.sap_sequencia === sequencia
            );
            if (!originalItem) return prev;

            if (precoEditado === originalItem.sap_precosolicitado) {
                const copy = { ...prev };
                delete copy[key];
                return copy;
            } else {
                return { ...prev, [key]: precoEditado };
            }
        });
    };

    // Aprovação Individual
    const confirmAprovar = (data, loja, sequencia, codigo, descricao, preco) => {
        const key = `${loja}-${codigo}-${sequencia}`;
        const precoUsar = precosEditados[key] !== undefined ? precosEditados[key] : parseFloat(preco);

        Swal.fire({
            title: 'Confirmar Liberação?',
            html: `<div class="text-start fs-6">
              <p><b>Produto:</b> ${codigo} - ${descricao}</p>
              <p><b>Loja:</b> Filial ${String(loja).padStart(2, '0')}</p>
              <p class="mb-0"><b>Preço Autorizado:</b> <span class="text-success fw-bold">R$ ${formatNumber(precoUsar)}</span></p>
             </div>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sim, Aprovar',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#10b981',
            cancelButtonColor: '#64748b',
            customClass: { popup: 'rounded-4' }
        }).then((result) => {
            if (result.isConfirmed) {
                const payload = {
                    chapa,
                    sequencia,
                    loja,
                    codigo,
                    datasol: data,
                    inicio: null,
                    fim: null,
                    preco: formatNumber(precoUsar),
                    status: "A"
                };

                api.post('/SAP/atualiza', payload)
                    .then(() => {
                        Swal.fire({
                            icon: 'success',
                            title: 'Preço Liberado!',
                            text: 'A alteração de preço foi gravada com sucesso.',
                            timer: 1800,
                            showConfirmButton: false
                        });
                        setPrecosEditados(prev => {
                            const copy = { ...prev };
                            delete copy[key];
                            return copy;
                        });
                        setAtualizado(Date.now());
                    })
                    .catch(err => {
                        Swal.fire('Falha na Liberação', err.response?.data?.erro || "Erro ao conectar com a API.", 'error');
                    });
            }
        });
    };

    // Negação Individual
    const confirmNegar = (data, loja, sequencia, codigo, descricao, preco) => {
        Swal.fire({
            title: 'Reprovar Preço?',
            text: `Informe o motivo do indeferimento para o item: ${descricao}`,
            input: 'textarea',
            inputPlaceholder: 'Escreva o motivo da recusa...',
            inputAttributes: { 'aria-label': 'Motivo da recusa' },
            showCancelButton: true,
            confirmButtonText: 'Confirmar Recusa',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#991b1b',
            cancelButtonColor: '#64748b',
            customClass: { popup: 'rounded-4' },
            inputValidator: (value) => {
                if (!value || !value.trim()) {
                    return 'Você precisa informar o motivo do indeferimento!';
                }
            }
        }).then((result) => {
            if (result.isConfirmed) {
                const payload = {
                    chapa,
                    sequencia,
                    loja,
                    codigo,
                    datasol: data,
                    inicio: null,
                    fim: null,
                    preco: 0,
                    status: "N",
                    motivo: result.value
                };

                api.post('/SAP/atualiza', payload)
                    .then(() => {
                        Swal.fire({
                            icon: 'info',
                            title: 'Solicitação Indeferida',
                            timer: 1800,
                            showConfirmButton: false
                        });
                        setAtualizado(Date.now());
                    })
                    .catch(err => {
                        Swal.fire('Erro', err.response?.data?.erro || "Falha ao registrar recusa.", 'error');
                    });
            }
        });
    };

    // Seleção
    const handleSelecionar = (checked, item) => {
        if (checked) {
            setSelecionados(prev => [...prev, item]);
        } else {
            setSelecionados(prev => prev.filter(s => s.loja !== item.loja || s.codigo !== item.codigo || s.sequencia !== item.sequencia));
        }
    };

    const selecionarTodos = () => {
        const todos = tabelas.map(tabela => {
            const key = `${tabela.sap_loja}-${tabela.sap_codigo}-${tabela.sap_sequencia}`;
            const preco = precosEditados[key] !== undefined ? precosEditados[key] : tabela.sap_precosolicitado;
            return {
                loja: tabela.sap_loja,
                codigo: tabela.sap_codigo,
                datasol: tabela.sap_data,
                sequencia: tabela.sap_sequencia,
                preco,
                descricao: tabela.sap_descricao
            };
        });
        setSelecionados(todos);
    };

    const desmarcarTodos = () => {
        setSelecionados([]);
    };

    // Autorizar Todos os Selecionados em Lote
    const autorizarTodosSelecionados = () => {
        if (selecionados.length === 0) return;

        Swal.fire({
            title: `Autorizar ${selecionados.length} Item(ns)?`,
            text: "Todos os produtos selecionados serão aprovados com os valores indicados.",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sim, Autorizar Todos',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#0284c7',
            cancelButtonColor: '#64748b',
            customClass: { popup: 'rounded-4' }
        }).then(async (result) => {
            if (result.isConfirmed) {
                Swal.fire({
                    title: 'A processar liberações...',
                    text: 'Aguarde um momento.',
                    allowOutsideClick: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });

                let sucessos = 0;
                for (const item of selecionados) {
                    const payload = {
                        chapa,
                        loja: item.loja,
                        codigo: item.codigo,
                        datasol: item.datasol,
                        sequencia: item.sequencia,
                        preco: formatNumber(item.preco),
                        status: "A",
                        inicio: null,
                        fim: null
                    };
                    try {
                        await api.post('/SAP/atualiza', payload);
                        sucessos++;
                    } catch (err) {
                        console.error("Erro ao autorizar em lote item:", item, err);
                    }
                }

                Swal.fire({
                    icon: 'success',
                    title: 'Aprovação Concluída',
                    text: `${sucessos} de ${selecionados.length} itens foram autorizados com sucesso.`
                });

                setSelecionados([]);
                setAtualizado(Date.now());
            }
        });
    };

    const todosEstaoSelecionados = tabelas.length > 0 && selecionados.length === tabelas.length;

    return (
        <div className="sap-page-wrapper">
            <Menu modulo="SAP" />

            <div className="container-fluid px-3 px-md-4 mt-4 max-width-container pb-5">

                {/* CABEÇALHO DO MÓDULO */}
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-2 border-bottom gap-2">
                    <div>
                        <h5 className="fw-bold m-0 text-dark d-flex align-items-center gap-2">
                            <FaTag className="text-custom-red" />
                            Liberação de Alteração de Preços (SAP)
                        </h5>
                        <span className="small text-muted">
                            Gerencie e aprove solicitações de alteração de preço pendentes.
                        </span>
                    </div>

                    <div className="d-flex align-items-center gap-2">
                        <button
                            className="btn btn-outline-secondary btn-sm fw-semibold"
                            onClick={carregarSolicitacoes}
                            disabled={!removeLoading}
                        >
                            <FaUndoAlt className="me-1" /> Atualizar
                        </button>
                    </div>
                </div>

                {/* BARRA DE AÇÕES BATCH / SELEÇÃO */}
                {tabelas.length > 0 && (
                    <div className="card border-0 shadow-sm rounded-3 mb-4 bg-white p-2 px-3">
                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                            <div className="d-flex align-items-center gap-2">
                                {!todosEstaoSelecionados ? (
                                    <button className="btn btn-outline-primary btn-sm fw-semibold rounded-3" onClick={selecionarTodos}>
                                        <FaCheckDouble className="me-1" /> Selecionar Todos ({tabelas.length})
                                    </button>
                                ) : (
                                    <button className="btn btn-outline-danger btn-sm fw-semibold rounded-3" onClick={desmarcarTodos}>
                                        <FaTimes className="me-1" /> Desmarcar Todos
                                    </button>
                                )}

                                {selecionados.length > 0 && (
                                    <span className="badge bg-light text-dark border px-2 py-1 fs-7 fw-semibold">
                                        {selecionados.length} selecionado(s)
                                    </span>
                                )}
                            </div>

                            {selecionados.length > 0 && (
                                <button className="btn btn-success btn-sm fw-bold rounded-3 px-3" onClick={autorizarTodosSelecionados}>
                                    <FaCheck className="me-1" /> Autorizar Selecionados ({selecionados.length})
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* SPINNER LOADING */}
                {!removeLoading && (
                    <div className="text-center py-5">
                        <div className="spinner-border text-custom-red" role="status" style={{ width: '2.5rem', height: '2.5rem' }}>
                            <span className="visually-hidden">A carregar...</span>
                        </div>
                        <div className="text-muted fw-semibold mt-2 small">Buscando solicitações pendentes...</div>
                    </div>
                )}

                {/* LISTAGEM DE CARDS AJUSTADA */}
                {removeLoading && (
                    <div className="row g-3">
                        {tabelas.map((tabela) => (
                            <div
                                className="col-xl-2 col-lg-3 col-md-4 col-sm-6 col-12"
                                key={`${tabela.sap_loja}_${tabela.sap_codigo}_${tabela.sap_sequencia}`}
                            >
                                <SapCard
                                    {...tabela}
                                    clickAprovar={confirmAprovar}
                                    clickNegar={confirmNegar}
                                    onSelecionar={handleSelecionar}
                                    selecionado={selecionados.some(s => s.loja === tabela.sap_loja && s.codigo === tabela.sap_codigo && s.sequencia === tabela.sap_sequencia)}
                                    onprecoEditadoChange={onPrecoEditadoChange}
                                />
                            </div>
                        ))}
                    </div>
                )}

            </div>

            {/* FLOATING ACTION BUTTON PARA DISPOSITIVOS MÓVEIS */}
            {selecionados.length > 0 && (
                <div className="sap-fab-container d-md-none">
                    <button className="btn btn-success fw-bold shadow-lg rounded-pill px-4 py-2" onClick={autorizarTodosSelecionados}>
                        <FaCheck className="me-1" /> Aprovar {selecionados.length}
                    </button>
                </div>
            )}

            {removeLoading && <Footer className="footer navbar-fixed-bottom" />}
        </div>
    );
}

export default Sap;